/**
 * Local-first mutations for COHORTS and PROGRAMS (F2-A).
 *
 * They write to PowerSync → write-path (`db/sync.rs`), which applies the
 * authz (ManageAcademics with campus scoping, or lead teacher) and the
 * server-authoritative enrollment rules (transitions, capacity, waitlist).
 * ACTIVATION and waitlist promotion also have immediate REST endpoints
 * (/api/cohorts/...) for the approval flows.
 */

import {
  cohort,
  cohort_enrollment,
  cohort_group,
  cohort_group_member,
  cohort_module_setting,
  cohort_teacher,
  eq,
  type PowerSyncDrizzleDB,
  program,
  program_course,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";
import { DEFAULT_COHORT_MODALITY } from "../verticals";
import { DEFAULT_PROGRAM_KIND, type ProgramKind } from "../programs/kinds";

// ─── Programs ───────────────────────────────────────────────────────────────

export async function createProgramMutation(
  db: PowerSyncDrizzleDB,
  params: {
    organizationId: string;
    name?: string;
    description?: string;
    kind?: ProgramKind;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(program).values({
    id,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nuevo programa",
    description: params.description ?? null,
    kind: params.kind ?? DEFAULT_PROGRAM_KIND,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * `organizationId` is the caller's ACTIVE org (`useActiveOrg`), not data from
 * `program` itself — tenant isolation (see `verified-write.ts`, `scope`
 * section): if `programId` arrived referring to a program from ANOTHER
 * organization (stale deep-link, id dragged along after a switch), the
 * `scope` makes the UPDATE match nothing instead of writing over someone else's row.
 */
export async function updateProgramMutation(
  db: PowerSyncDrizzleDB,
  programId: string,
  updates: Partial<{ name: string; description: string | null; kind: string }>,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    program,
    program.id,
    programId,
    updates,
    withScope(options, [{ column: program.organization_id, value: organizationId }]),
  );
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to delete a program). */
export async function deleteProgramMutation(
  db: PowerSyncDrizzleDB,
  programId: string,
): Promise<void> {
  await db
    .update(program)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(program.id, programId));
}

export async function addProgramCourseMutation(
  db: PowerSyncDrizzleDB,
  params: {
    programId: string;
    organizationId: string;
    courseId: string;
    position?: number;
    required?: boolean;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(program_course).values({
    id,
    program_id: params.programId,
    organization_id: params.organizationId,
    course_id: params.courseId,
    position: params.position ?? 0,
    required: params.required === false ? 0 : 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function removeProgramCourseMutation(
  db: PowerSyncDrizzleDB,
  programCourseId: string,
): Promise<void> {
  await db.delete(program_course).where(eq(program_course.id, programCourseId));
}

// ─── Cohorts ────────────────────────────────────────────────────────────────

export interface CreateCohortInput {
  organizationId: string;
  courseId: string;
  programId?: string | null;
  campusId?: string | null;
  code?: string;
  name?: string;
  modality?: "in_person" | "online_live" | "hybrid" | "self_paced";
  startDate?: string | null;
  endDate?: string | null;
  capacity?: number;
  enrollmentRule?: "open" | "approval" | "waitlist";
  status?: "draft" | "open" | "running" | "completed" | "cancelled";
  gradingScheme?: string | null;
}

export async function createCohortMutation(
  db: PowerSyncDrizzleDB,
  input: CreateCohortInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(cohort).values({
    id,
    organization_id: input.organizationId,
    course_id: input.courseId,
    program_id: input.programId ?? null,
    campus_id: input.campusId ?? null,
    code: input.code ?? null,
    name: input.name?.trim() || "Nueva cohorte",
    // §L2: online-live by factory default (mirror of `cohort.modality`'s
    // DEFAULT in migration 00083). The no-decisions path leads to what the
    // academy sells: online courses.
    modality: input.modality ?? DEFAULT_COHORT_MODALITY,
    start_date: input.startDate ?? null,
    end_date: input.endDate ?? null,
    capacity: input.capacity ?? 0,
    enrollment_rule: input.enrollmentRule ?? "open",
    status: input.status ?? "draft",
    grading_scheme: input.gradingScheme ?? null,
    config: "{}",
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** `organizationId`: see the `updateProgramMutation` note (ACTIVE org, not `cohort`'s). */
export async function updateCohortMutation(
  db: PowerSyncDrizzleDB,
  cohortId: string,
  updates: Partial<{
    name: string;
    code: string | null;
    modality: string;
    start_date: string | null;
    end_date: string | null;
    capacity: number;
    enrollment_rule: string;
    status: string;
    grading_scheme: string | null;
    grading_model_id: string | null;
    campus_id: string | null;
    program_id: string | null;
  }>,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort,
    cohort.id,
    cohortId,
    updates,
    withScope(options, [{ column: cohort.organization_id, value: organizationId }]),
  );
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to delete a cohort). */
export async function deleteCohortMutation(
  db: PowerSyncDrizzleDB,
  cohortId: string,
): Promise<void> {
  await db
    .update(cohort)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(cohort.id, cohortId));
}

// ─── Teachers ──────────────────────────────────────────────────────────────

export async function addCohortTeacherMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    userId: string;
    role?: "lead" | "assistant";
  },
): Promise<string> {
  const id = generateId();
  await db.insert(cohort_teacher).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    role: params.role ?? "lead",
    created_at: nowISO(),
  });
  return id;
}

export async function removeCohortTeacherMutation(
  db: PowerSyncDrizzleDB,
  cohortTeacherId: string,
): Promise<void> {
  await db.delete(cohort_teacher).where(eq(cohort_teacher.id, cohortTeacherId));
}

// ─── Enrollment ───────────────────────────────────────────────────────────────

/**
 * The STUDENT requests local-first enrollment (rule 'open'): creates their
 * `pre_enrolled` row — the write-path validates the cohort's rule/state and
 * a manager (or the REST endpoint) activates it with capacity control.
 */
export async function requestCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId: string; campusId?: string | null; userId: string },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(cohort_enrollment).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    campus_id: params.campusId ?? null,
    user_id: params.userId,
    status: "pre_enrolled",
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Proposes a status TRANSITION (activate/withdraw/complete/…). The server
 * validates the transition and executes the effects (capacity, content
 * enrollment, waitlist promotion); an invalid change simply doesn't apply.
 */
/** `organizationId`: see the `updateProgramMutation` note (ACTIVE org, not the enrollment's). */
export async function transitionCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
  status: "pre_enrolled" | "waitlisted" | "active" | "completed" | "withdrawn",
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    cohort_enrollment,
    cohort_enrollment.id,
    enrollmentId,
    { status },
    withScope(options, [{ column: cohort_enrollment.organization_id, value: organizationId }]),
  );
}

/**
 * Notes/final grade of the enrollment record (managers only, server validates).
 * ⚠️ Dead code: no callers in `apps/web`.
 */
export async function updateCohortEnrollmentMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
  updates: Partial<{
    notes: string | null;
    final_grade: number | null;
    final_grade_scheme: string | null;
  }>,
): Promise<void> {
  await db
    .update(cohort_enrollment)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(cohort_enrollment.id, enrollmentId));
}

// ─── Groups ──────────────────────────────────────────────────────────────────

export async function createCohortGroupMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId: string; name?: string },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(cohort_group).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nuevo grupo",
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web`. */
export async function deleteCohortGroupMutation(
  db: PowerSyncDrizzleDB,
  groupId: string,
): Promise<void> {
  await db
    .update(cohort_group)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(cohort_group.id, groupId));
}

export async function addCohortGroupMemberMutation(
  db: PowerSyncDrizzleDB,
  params: { groupId: string; cohortId: string; organizationId: string; userId: string },
): Promise<string> {
  const id = generateId();
  await db.insert(cohort_group_member).values({
    id,
    group_id: params.groupId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    created_at: nowISO(),
  });
  return id;
}

export async function removeCohortGroupMemberMutation(
  db: PowerSyncDrizzleDB,
  memberId: string,
): Promise<void> {
  await db.delete(cohort_group_member).where(eq(cohort_group_member.id, memberId));
}

// ─── Per-module and per-cohort settings ────────────────────────────────────────

/**
 * Local upsert of the setting (cohort_id, module_id): due/open/visibility.
 * PowerSync doesn't support UPSERT on views → manual UPDATE-or-INSERT.
 *
 * ⚠️ Class 2 (POWERSYNC_VERIFIED_WRITE.md §3): the SELECT that looks up the row
 * by (cohort_id, module_id) —with a RANDOM id if it doesn't exist— lives
 * inside the window. A false "doesn't exist" doesn't lose the data, it
 * duplicates the module's setting. No id known ahead of time nor a unique
 * index for `ON CONFLICT DO UPDATE`. Documented and characterized, not shielded.
 */
export async function upsertCohortModuleSettingMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    moduleId: string;
    organizationId: string;
    dueAt?: string | null;
    openAt?: string | null;
    visibility?: "visible" | "hidden" | null;
    config?: unknown;
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: cohort_module_setting.id, module_id: cohort_module_setting.module_id })
    .from(cohort_module_setting)
    .where(eq(cohort_module_setting.cohort_id, params.cohortId));
  const found = rows.find((r) => r.module_id === params.moduleId);
  const values: Record<string, unknown> = {
    due_at: params.dueAt ?? null,
    open_at: params.openAt ?? null,
    visibility: params.visibility ?? null,
    config: JSON.stringify(params.config ?? {}),
    updated_at: now,
  };
  if (found) {
    await db
      .update(cohort_module_setting)
      .set(values)
      .where(eq(cohort_module_setting.id, found.id));
    return found.id;
  }
  const id = generateId();
  await db.insert(cohort_module_setting).values({
    id,
    cohort_id: params.cohortId,
    module_id: params.moduleId,
    organization_id: params.organizationId,
    created_at: now,
    ...values,
  });
  return id;
}
